import type { PropsWithChildren } from "react";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import type { ElementFormatType, TextFormatType } from "lexical";

import { $isCodeNode, CODE_LANGUAGE_MAP } from "@lexical/code";
import { $isLinkNode } from "@lexical/link";
import { $isListNode, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode } from "@lexical/rich-text";
import { $getSelectionStyleValueForProperty } from "@lexical/selection";
import { $isTableNode, $isTableSelection } from "@lexical/table";
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from "@lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
} from "lexical";

import { blockTypeToBlockName, getSelectedNode } from "./utils";

interface ToolbarState {
  rootType?: "table" | "root";
  blockType?: string;
  codeLanguage?: string;
  canUndo?: boolean;
  canRedo?: boolean;
  isLink?: boolean;
  fontSize?: string;
}

interface UseToolbarStateApi extends ToolbarState {
  hasFormat: (format: TextFormatType | ElementFormatType) => boolean;
}
function useToolbarState(): UseToolbarStateApi {
  const [editor] = useLexicalComposerContext();
  const [toolbarState, setToolbarState] = useState<ToolbarState>({});
  const updateToolbarState = useCallback(
    <TKey extends keyof ToolbarState>(key: TKey, value: ToolbarState[TKey]) =>
      setToolbarState((p) => ({ ...p, [key]: value })),
    [],
  );

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      const node = getSelectedNode(selection);
      const parent = node.getParent();
      const isLink = $isLinkNode(parent) || $isLinkNode(node);
      updateToolbarState("isLink", isLink);

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        updateToolbarState("rootType", "table");
      } else {
        updateToolbarState("rootType", "root");
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();

          updateToolbarState("blockType", type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            updateToolbarState(
              "blockType",
              type as keyof typeof blockTypeToBlockName,
            );
          }
          if ($isCodeNode(element)) {
            const language = element.getLanguage()!;
            updateToolbarState(
              "codeLanguage",
              language ? (CODE_LANGUAGE_MAP[language] ?? language) : "",
            );
            return;
          }
        }
      }

      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        updateToolbarState(
          "fontSize",
          $getSelectionStyleValueForProperty(selection, "font-size", "15px"),
        );
      }
    }
  }, [editor, updateToolbarState]);

  const hasFormat = useCallback<UseToolbarStateApi["hasFormat"]>(
    (format) => {
      return editor.getEditorState().read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
          return selection.hasFormat(format as TextFormatType);
        }

        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          let element =
            anchorNode.getKey() === "root"
              ? anchorNode
              : $findMatchingParent(anchorNode, (e) => {
                  const parent = e.getParent();
                  return parent !== null && $isRootOrShadowRoot(parent);
                });

          if (element === null) {
            element = anchorNode.getTopLevelElementOrThrow();
          }

          const node = getSelectedNode(selection);
          const parent = node.getParent();

          let matchingParent;
          if ($isLinkNode(parent)) {
            // If node is a link, we need to fetch the parent paragraph node to set format
            matchingParent = $findMatchingParent(
              node,
              (parentNode) =>
                $isElementNode(parentNode) && !parentNode.isInline(),
            );
          }

          return $isElementNode(matchingParent)
            ? matchingParent.getFormatType() === format
            : $isElementNode(node)
              ? node.getFormatType() === format
              : parent?.getFormatType() === format;
        }

        return false;
      });
    },
    [editor],
  );

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateToolbar();
    });
  }, [editor, updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          updateToolbarState("canUndo", payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          updateToolbarState("canRedo", payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [updateToolbar, editor, updateToolbarState]);

  return {
    ...toolbarState,
    hasFormat,
  };
}

const ToolbarStateContext = createContext<UseToolbarStateApi | null>(null);

export function useToolbarStateContext() {
  const toolbarStateContext = useContext(ToolbarStateContext);
  if (!toolbarStateContext) {
    throw new Error(
      "'useToolbarStateContext' must be used within 'ToolbarStateContextProvider'",
    );
  }
  return toolbarStateContext;
}

export function ToolbarStateProvider({ children }: PropsWithChildren) {
  const toolbarState = useToolbarState();
  return (
    <ToolbarStateContext value={toolbarState}>{children}</ToolbarStateContext>
  );
}
